import { enUS } from 'date-fns/locale';
import {
  formatRelative,
  format,
  formatISO,
  formatDistanceToNow,
  parseISO,
  isValid,
  addDays,
  addMonths,
} from 'date-fns';
import { UTCDate } from '@date-fns/utc';

const DAYSOFWEEK = [
  {
    value: 0,
    label: 'Sunday',
    abbreviation: 'Su',
  },
  {
    value: 1,
    label: 'Monday',
    abbreviation: 'M',
  },
  {
    value: 2,
    label: 'Tuesday',
    abbreviation: 'Tu',
  },
  {
    value: 3,
    label: 'Wednesday',
    abbreviation: 'W',
  },
  {
    value: 4,
    label: 'Thursday',
    abbreviation: 'Th',
  },
  {
    value: 5,
    label: 'Friday',
    abbreviation: 'F',
  },
  {
    value: 6,
    label: 'Saturday',
    abbreviation: 'S',
  },
];

const formatRelativeLocale = {
  lastWeek: "'Last' eeee",
  yesterday: "'Yesterday'",
  today: "'Today'",
  tomorrow: "'Tomorrow'",
  nextWeek: 'eeee',
  other: 'M/d/yy',
};

const locale = {
  ...enUS,
  formatRelative: (token) => formatRelativeLocale[token],
};

// Accepts a Date, an ISO string, or an epoch number. Uses parseISO for strings
// so a date-only value ('2024-01-15') resolves to LOCAL midnight (matching the
// previous moment behavior) rather than the UTC midnight `new Date(str)` gives.
const toDate = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  if (value instanceof Date) {
    return value;
  }
  if (typeof value === 'number') {
    return new Date(value);
  }
  if (typeof value === 'string') {
    return parseISO(value);
  }
  return null;
};

class DateUtil {
  // fns https://www.unicode.org/reports/tr35/tr35-dates.html#Date_Field_Symbol_Table
  static get LDML_DISPLAY_DATE_TIME_FORMAT() {
    return 'MM/dd/yyy hh:mm aa';
  }

  static get LDML_DISPLAY_DATE_TIME_INPUT_FORMAT() {
    return 'MM/dd/yyyy hh:mm aa';
  }

  // fns https://www.unicode.org/reports/tr35/tr35-dates.html#Date_Field_Symbol_Table
  static get LDML_DISPLAY_FORMAT() {
    return 'MM/dd/yyyy';
  }

  static get LDML_FORM_DATE_FORMAT() {
    return 'yyyy-MM-dd';
  }

  // Moment-token format constants, retained ONLY for the call sites still on
  // moment (DynamicTable.js). Delete these once DynamicTable.js is migrated.
  static get DISPLAY_FORMAT() {
    return 'MM/DD/YYYY';
  }

  static get FORM_DATE_FORMAT() {
    return 'YYYY-MM-DD';
  }

  static formatDateForUser(date) {
    const d = toDate(date);
    if (d && isValid(d)) {
      return format(d, DateUtil.LDML_DISPLAY_FORMAT);
    }
    return '';
  }

  static formatDateTimeFromDB(date) {
    const d = toDate(date);
    if (d && isValid(d)) {
      return format(d, DateUtil.LDML_DISPLAY_DATE_TIME_FORMAT);
    }
    return '';
  }

  // Renders in UTC, preserving the moment.utc() behavior the old code relied on.
  static formatFromDB(date) {
    const d = toDate(date);
    if (d && isValid(d)) {
      return format(new UTCDate(d), DateUtil.LDML_DISPLAY_FORMAT);
    }
    return '';
  }

  static formatNowForDB() {
    return formatISO(new Date());
  }

  static formatFromNow(date) {
    const d = toDate(date);
    if (d && isValid(d)) {
      return formatDistanceToNow(d, { addSuffix: true });
    }
    return '';
  }

  static dayOfWeekToAbbreviation(dayOfWeek) {
    const day = DAYSOFWEEK.filter((d) => d.value === dayOfWeek);
    if (day.length > 0) {
      return day[0].abbreviation;
    }
    return null;
  }

  static formatRelativeToday(date) {
    const d = toDate(date);
    if (d && isValid(d)) {
      return formatRelative(d, new Date(), { locale });
    }
    return '';
  }

  static listUnitsBetween(startDate, endDate, units = 'days') {
    const start = toDate(startDate);
    const end = toDate(endDate);
    if (!start || !end || !isValid(start) || !isValid(end) || start > end) {
      return [];
    }
    // Mirror the original moment loop: step by 1 unit, preserving day-of-month.
    const step = units === 'days' ? addDays : addMonths;
    const days = [];
    let currentDate = start;
    while (currentDate <= end) {
      days.push(format(currentDate, DateUtil.LDML_FORM_DATE_FORMAT));
      currentDate = step(currentDate, 1);
    }
    return days;
  }
}

export { DateUtil, DAYSOFWEEK };
export default DateUtil;
